import React from 'react';
import { Text, TagBadge, Button } from '@chatsdet/ui';
import { Play, Pause, Trash, ArrowsClockwise, Clock } from '@phosphor-icons/react';
import {
  useToggleScheduleRule,
  useRunScheduleNow,
  useDeleteScheduleRule,
  formatNextRun,
} from '../../../features/scheduling';
import {
  useSuiteSchedules,
  describeScheduleTarget,
  type TestSuiteSchedule,
} from '../../../features/test-suites';
import { EditScheduleButton, ScheduleSuiteModal } from '../../../features/test-suites/ScheduleSuiteModal';
import type { ExecutionStatus } from '../../../features/scheduling/executionStatus';

// Typed against the shared `ExecutionStatus` union (not a local string union)
// so adding a new status there fails this file to compile until it's handled
// here too — the guard that `skipped` slipped past before.
const LAST_RUN_BADGE: Record<ExecutionStatus, { label: string; color: 'green' | 'gray' | 'orange' | 'teal' }> = {
  completed: { label: 'Passed', color: 'green' },
  failed: { label: 'Failed', color: 'orange' },
  running: { label: 'Running', color: 'teal' },
  scheduled: { label: 'Scheduled', color: 'gray' },
  pending: { label: 'Pending', color: 'gray' },
  canceled: { label: 'Canceled', color: 'gray' },
  superseded: { label: 'Superseded', color: 'gray' },
  skipped: { label: 'Skipped', color: 'gray' },
};

/** `lastRunStatus` comes back as a plain `string` from the API; narrow it at
 * this single boundary rather than widening the exhaustive map above. */
function getLastRunBadge(status: string | null): { label: string; color: 'green' | 'gray' | 'orange' | 'teal' } | null {
  if (!status) return null;
  return LAST_RUN_BADGE[status as ExecutionStatus] ?? null;
}

function ScheduledRow({ schedule }: { schedule: TestSuiteSchedule }) {
  const toggleMutation = useToggleScheduleRule();
  const runNowMutation = useRunScheduleNow();
  const deleteMutation = useDeleteScheduleRule();
  const lastRun = getLastRunBadge(schedule.lastRunStatus);

  // Spin the run-now icon on click for clear feedback (the POST returns fast).
  const [spinning, setSpinning] = React.useState(false);
  React.useEffect(() => {
    if (!spinning) return;
    const t = window.setTimeout(() => setSpinning(false), 1200);
    return () => window.clearTimeout(t);
  }, [spinning]);
  const handleRunNow = () => {
    setSpinning(true);
    runNowMutation.mutate(schedule.ruleId);
  };
  const isSpinning = spinning || runNowMutation.isPending;

  return (
    <tr className="border-b border-border last:border-b-0">
      <td className="py-2 px-3">
        <Text size="sm" weight="medium" className="truncate">
          {schedule.description}
        </Text>
      </td>
      <td className="py-2 px-3">
        <Text size="sm" className="text-muted-foreground truncate">
          {describeScheduleTarget(schedule)}
        </Text>
      </td>
      <td className="py-2 px-3">
        <Text size="sm" className="text-muted-foreground">
          {schedule.enabled ? formatNextRun(schedule.nextRunAt) : 'Paused'}
        </Text>
      </td>
      <td className="py-2 px-3">
        {lastRun ? (
          <TagBadge name={lastRun.label} color={lastRun.color} size="sm" hideDot />
        ) : (
          <Text size="sm" className="text-muted-foreground">
            —
          </Text>
        )}
      </td>
      <td className="py-2 px-3">
        <div className="flex items-center justify-end gap-1">
          <EditScheduleButton schedule={schedule} />
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip={schedule.enabled ? 'Pause' : 'Resume'}
            iconLeft={schedule.enabled ? <Pause size={15} /> : <Play size={15} />}
            onPress={() => toggleMutation.mutate(schedule.ruleId)}
            isPending={toggleMutation.isPending}
          />
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="Run now"
            iconLeft={
              <ArrowsClockwise size={15} className={isSpinning ? 'animate-spin' : undefined} />
            }
            onPress={handleRunNow}
            isPending={runNowMutation.isPending}
          />
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tone="destructive"
            tooltip="Delete schedule"
            iconLeft={<Trash size={15} />}
            onPress={() => deleteMutation.mutate(schedule.ruleId)}
            isPending={deleteMutation.isPending}
          />
        </div>
      </td>
    </tr>
  );
}

/**
 * "Scheduled" section shown above the suite list. Lists every recurring test-run
 * schedule for the org (named suite / all published / by tag), each with
 * edit (timing and alerts), pause/resume, run-now, and delete. A "New schedule" button opens the
 * target-picker modal (all published / by tag); a named-suite schedule is
 * created from that suite's builder instead. Always rendered, with an empty
 * state when nothing is scheduled.
 */
export function SuiteScheduledSection() {
  const { data, isLoading } = useSuiteSchedules();
  const schedules = data?.schedules ?? [];

  return (
    <div className="mb-6">
      <div className="mb-2 flex items-center justify-between">
        <Text weight="semibold">Scheduled</Text>
        <ScheduleSuiteModal
          trigger={
            <Button variant="secondary" size="sm" iconLeft={<Clock size={14} />}>
              New schedule
            </Button>
          }
        />
      </div>
      {isLoading ? null : schedules.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-background px-4 py-8 text-center">
          <Clock size={28} className="mx-auto mb-2 text-muted-foreground" />
          <Text size="sm" className="text-muted-foreground">
            No schedules yet. Schedule all published workflows or workflows by tag to run on a
            recurring cadence, or schedule a named suite from its builder.
          </Text>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden bg-background">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border bg-muted">
                <th className="py-2 px-3 font-medium text-xs text-muted-foreground">Schedule</th>
                <th className="py-2 px-3 font-medium text-xs text-muted-foreground">Target</th>
                <th className="py-2 px-3 font-medium text-xs text-muted-foreground">Next run</th>
                <th className="py-2 px-3 font-medium text-xs text-muted-foreground">Last run</th>
                <th className="py-2 px-3" />
              </tr>
            </thead>
            <tbody>
              {schedules.map((s) => (
                <ScheduledRow key={s.ruleId} schedule={s} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
